/**
 * This is only a button for posting new assignments.  It can create an
 * assignment either by student List or by individual students.  It is mostly
 * a bridge to the GraphQL mutations.
 * TODO(jangmi, CLASS-4946): Separate create/update mutations for
 * maintainability.
 **/
import {t} from "@lingui/core/macro";
import * as React from "react";
import {useDispatch, useSelector} from "react-redux";

import Button from "@khanacademy/wonder-blocks-button";
import {useIsMounted} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {mutationBegin, mutationEnd} from "@khan/assignments-store";
import {
    type Assignment,
    type NewAssignmentSubmitParameters,
    type State,
} from "@khan/assignments-types";
import {
    getContentTitle,
    getTotalStudentCount,
    isValidToDraft,
    isValidToPost,
} from "@khan/assignments-utils";
import {useMutation} from "@khan/data-gql";
import {Errors, KAError} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";

import {
    CREATE_ASSIGNMENTS_MUTATION,
    UPDATE_ASSIGNMENT_MUTATION,
} from "./queries.ts";
import {
    assignmentToCommonVariables,
    studentsDiff,
    triggerCreateAssignmentNotification,
    triggerDuplicateErrorToast,
    triggerUpdateAssignmentErrorNotification,
    triggerUpdateAssignmentNotification,
} from "./utils.tsx";

interface OwnProps {
    subjectSlug?: string;
    buttonText?: string;
    loadingText?: string;
    isDraft: boolean;
    // Refetch the assignment table after update or
    // clear editingAssignment in store
    onMutationSuccess?: () => void;
    onMutationSubmit?: (params: NewAssignmentSubmitParameters) => unknown;
    disabled?: boolean;
}

const NewAssignmentPostButton = (props: OwnProps) => {
    const {
        buttonText = "",
        loadingText = "",
        disabled,
        isDraft,
        subjectSlug,
        onMutationSuccess,
        onMutationSubmit,
    } = props;

    // Redux state
    const assignment = useSelector(
        (state: State) => state.editingAssignment.assignment,
    );
    const classDescriptors = useSelector(
        (state: State) => state.editingAssignment.classDescriptors || [],
    );
    const qualifiedContentDescriptorsList = useSelector(
        (state: State) =>
            state.editingAssignment.qualifiedContentDescriptorsList || [],
    );
    const saving = useSelector((state: State) => state.saving);
    const studentLists = useSelector(
        (state: State) => state.studentLists || [],
    );

    // Redux dispatch
    const dispatch = useDispatch();
    const onMutationBegin = React.useCallback(
        () => dispatch(mutationBegin()),
        [dispatch],
    );
    const onMutationEnd = React.useCallback(
        () => dispatch(mutationEnd()),
        [dispatch],
    );

    // Mutation hooks
    const [createMutate] = useMutation(CREATE_ASSIGNMENTS_MUTATION);
    const [updateMutate] = useMutation(UPDATE_ASSIGNMENT_MUTATION);

    // State hooks
    const [assignmentCreated, setAssignmentCreated] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const originalStudents = React.useMemo(
        () => assignment?.students || undefined,
        /* eslint-disable-next-line react-hooks/exhaustive-deps --
           We don't want this to re-run if the assignment changes because later on this file
           we compare this value against assignment.students so that we know anything changed
           or not. */
        [],
    );

    const isMounted = useIsMounted();

    // Effect to handle assignment changes (replaces UNSAFE_componentWillUpdate)
    React.useEffect(() => {
        if (!assignment) {
            return;
        }

        // Reset assignmentCreated flag when assignment changes
        setAssignmentCreated(false);
    }, [assignment]);

    // Helper functions
    const canPost = React.useCallback((): boolean | null | undefined => {
        return (
            !disabled &&
            !loading &&
            assignment &&
            isValidToPost(assignment, classDescriptors)
        );
    }, [disabled, loading, assignment, classDescriptors]);

    const canDraft = React.useCallback((): boolean | null | undefined => {
        return (
            !disabled &&
            !loading &&
            assignment &&
            isValidToDraft(assignment, classDescriptors)
        );
    }, [disabled, loading, assignment, classDescriptors]);

    const onCreateAssignments = React.useCallback(
        (
            assignment: Assignment,
            classDescriptors: Array<string>,
            qualifiedContentDescriptors: Array<string>,
        ): Promise<Array<Assignment>> => {
            publishAnalyticsEventForMinorConversion({
                eventType: "AssignmentCreatedTI",
                eventSchemaVersion: 1,
                assignmentType: "CONTENT",
            });

            // TODO (CLASS-10314) - Call updated CreateAssignment mutation with
            // assignment.configuredActivityInputs once the mutation accepts it.
            return createMutate({
                ...assignmentToCommonVariables(assignment),
                studentKaids: assignment.students.map(
                    (student) => student.kaid,
                ),
                classDescriptors,
                qualifiedContentDescriptorsList: qualifiedContentDescriptors,
                subjectSlug:
                    subjectSlug ||
                    // Set from the redux store editingAssignment. (Copy)
                    assignment.subjectSlug,
                questionSetType: assignment.questionSetType || "RANDOM",
                title: assignment.title || null,
                instructions: assignment.instructions || null,
                configuredActivityInputs: !assignment.configuredActivityInputs
                    ? null
                    : typeof assignment.configuredActivityInputs === "object"
                      ? JSON.stringify(assignment.configuredActivityInputs) // New assignments are objects
                      : assignment.configuredActivityInputs, // Copied assignments are already stringified
                creationKaLocale: RequestInfo.kaLocale,
            }).then((result) => {
                // TODO(jangmi, CLASS-4945): Don't use Promise chain to handle the
                // result.
                if (result.status !== "success" || !result.data) {
                    throw new KAError("Failed to mutate", Errors.Internal);
                }
                const {createAssignments} = result.data;
                return (
                    (createAssignments && createAssignments.assignments) ||
                    []
                )
                    .filter((assignment) => assignment != null)
                    .map(
                        (assignment): Assignment =>
                            // TODO(CLASS-12765): The assignment returned by the mutation
                            // is missing some fields from `Assignment` and some of the
                            // fields aren't the correct type.
                            assignment as unknown as Assignment,
                    );
            });
        },
        [createMutate, subjectSlug],
    );

    const onUpdateAssignment = React.useCallback(
        (assignment: Assignment) => {
            const {added, removed} = originalStudents
                ? studentsDiff(originalStudents, assignment.students)
                : {added: [], removed: []};
            return updateMutate({
                ...assignmentToCommonVariables(assignment),
                studentKaidsToAdd: added.map((student) => student.kaid),
                studentKaidsToRemove: removed.map((student) => student.kaid),
                assignmentId: assignment.id,
                studentListId:
                    (assignment.studentList && assignment.studentList.id) || "",
                contentDescriptors: assignment.contentDescriptors,
            }).then((result) => {
                // TODO(jangmi, CLASS-4945): Don't use Promise chain to handle
                // the result.
                if (result.status !== "success" || !result.data) {
                    throw new KAError("Failed to mutate", Errors.Internal);
                }

                const {updateAssignment} = result.data;
                return (
                    (updateAssignment &&
                        updateAssignment.assignment && [
                            // TODO(CLASS-12765): The assignment returned by the mutation
                            // is missing some fields from `Assignment` and some of the
                            // fields aren't the correct type.
                            updateAssignment.assignment as unknown as Assignment,
                        ]) ||
                    []
                );
            });
        },
        [updateMutate, originalStudents],
    );

    const createOrUpdateAssignment = React.useCallback(() => {
        if (
            !assignment ||
            !isValidToDraft(assignment, classDescriptors) ||
            loading
        ) {
            return;
        }

        // If we've already created exactly this assignment, throw an error.
        if (assignmentCreated) {
            triggerDuplicateErrorToast();
            return;
        }

        const wrappedAssignment = {
            ...assignment,
            isDraft,
        } as const;

        setLoading(true);
        onMutationBegin();

        const requestedContentTitle = getContentTitle(assignment);

        // Assigning from Saved tab uses updateAssignment mutation but it
        // should be displayed as "Assign" and trigger create assignment notif.
        // So we need to separate the mutation and display toast.
        const shouldUseCreateMutation = !wrappedAssignment.id;
        const shouldShowUpdateToast =
            assignment.id && assignment.isDraft === isDraft;

        let mutate:
            | ReturnType<typeof onCreateAssignments>
            | ReturnType<typeof onUpdateAssignment>;
        let requestDetails;
        if (shouldUseCreateMutation) {
            mutate = onCreateAssignments(
                wrappedAssignment,
                classDescriptors,
                qualifiedContentDescriptorsList,
            );
            requestDetails = [
                isDraft ? "SAVE" : "ASSIGN",
                requestedContentTitle,
                classDescriptors.length,
                qualifiedContentDescriptorsList.length,
            ];
        } else {
            mutate = onUpdateAssignment(wrappedAssignment);
        }

        mutate
            .then((result) => {
                if (!isMounted()) {
                    return;
                }

                setAssignmentCreated(true);
                setLoading(false);

                if (shouldShowUpdateToast) {
                    triggerUpdateAssignmentNotification(result[0]);
                } else {
                    triggerCreateAssignmentNotification(
                        result,
                        // @ts-expect-error - TS2556 - A spread argument must either have a tuple type or be passed to a rest parameter.
                        ...requestDetails,
                    );
                }

                if (onMutationSubmit) {
                    onMutationSubmit({
                        assignment: wrappedAssignment,
                        qualifiedContentDescriptors:
                            qualifiedContentDescriptorsList,
                        numStudentsInClass: getTotalStudentCount(
                            studentLists,
                            classDescriptors,
                        ),
                        createdAssignments: result,
                    });
                }

                if (onMutationSuccess) {
                    onMutationSuccess();
                }

                onMutationEnd();
            })
            .catch(() => {
                if (!isMounted()) {
                    return;
                }

                setLoading(false);
                if (shouldShowUpdateToast) {
                    triggerUpdateAssignmentErrorNotification(
                        requestedContentTitle,
                    );
                } else {
                    // @ts-expect-error - TS2556 - A spread argument must either have a tuple type or be passed to a rest parameter.
                    triggerCreateAssignmentNotification([], ...requestDetails);
                }

                if (onMutationSubmit) {
                    onMutationSubmit({
                        assignment: wrappedAssignment,
                        qualifiedContentDescriptors:
                            qualifiedContentDescriptorsList,
                        numStudentsInClass: getTotalStudentCount(
                            studentLists,
                            classDescriptors,
                        ),
                    });
                }

                onMutationEnd();
            });
    }, [
        assignment,
        classDescriptors,
        loading,
        assignmentCreated,
        isDraft,
        onMutationBegin,
        qualifiedContentDescriptorsList,
        onCreateAssignments,
        onUpdateAssignment,
        isMounted,
        onMutationSubmit,
        studentLists,
        onMutationSuccess,
        onMutationEnd,
    ]);

    const getDraftButtonText = (): string => {
        if (loading) {
            return t`Saving...`;
        }

        return t`Save draft`;
    };

    const renderAsDraft = (): React.ReactNode => {
        return (
            <Button
                kind="tertiary"
                disabled={saving || !canDraft()}
                onClick={createOrUpdateAssignment}
                style={{padding: spacing.medium_16}}
                testId="new-assignment-draft-button"
            >
                {getDraftButtonText()}
            </Button>
        );
    };

    const renderAsPost = (): React.ReactNode => {
        return (
            <Button
                disabled={saving || !canPost()}
                onClick={createOrUpdateAssignment}
                style={{width: 120}}
                testId="new-assignment-post-button"
                kind="primary"
            >
                {loading
                    ? loadingText || t`Assigning...`
                    : buttonText || t`Assign`}
            </Button>
        );
    };

    if (!assignment) {
        return null;
    }

    return isDraft ? renderAsDraft() : renderAsPost();
};

export default NewAssignmentPostButton;
